import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type InstallPromptOutcome = 'accepted' | 'dismissed';

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: InstallPromptOutcome; platform: string }>;
  prompt: () => Promise<void>;
}

interface PwaInstallState {
  deferredPrompt: BeforeInstallPromptEvent | undefined;
  shouldShowPrompt: boolean;
  hasDismissed: boolean;
  isInstalled: boolean;
  setDeferredPrompt: (event: BeforeInstallPromptEvent | undefined) => void;
  setInstalled: (installed: boolean) => void;
  hidePrompt: (options?: { permanent?: boolean }) => void;
  resetDismissal: () => void;
  reset: () => void;
}

export const usePwaInstallStore = create<PwaInstallState>()(
  persist(
    (set, _get) => ({
      deferredPrompt: undefined,
      shouldShowPrompt: false,
      hasDismissed: false,
      isInstalled: false,
      setDeferredPrompt: (event) => {
        if (!event) {
          set((state) => ({
            deferredPrompt: undefined,
            shouldShowPrompt: false,
            hasDismissed: state.hasDismissed,
          }));
          return;
        }

        event.preventDefault();

        set((state) => ({
          deferredPrompt: event,
          shouldShowPrompt: state.hasDismissed ? false : true,
        }));
      },
      setInstalled: (installed) => {
        set((state) => ({
          isInstalled: installed,
          shouldShowPrompt: installed ? false : state.shouldShowPrompt,
          deferredPrompt: installed ? undefined : state.deferredPrompt,
          hasDismissed: installed ? true : state.hasDismissed,
        }));
      },
      hidePrompt: (options) => {
        const permanent = options?.permanent ?? false;
        set((state) => ({
          shouldShowPrompt: false,
          hasDismissed: permanent ? true : state.hasDismissed,
          deferredPrompt: permanent ? undefined : state.deferredPrompt,
        }));
      },
      resetDismissal: () => {
        set((state) => ({
          hasDismissed: false,
          shouldShowPrompt: state.deferredPrompt ? true : state.shouldShowPrompt,
        }));
      },
      reset: () => {
        set(() => ({
          deferredPrompt: undefined,
          shouldShowPrompt: false,
          hasDismissed: false,
          isInstalled: false,
        }));
      },
    }),
    {
      name: 'pwa-install-store',
      partialize: (state) => ({
        hasDismissed: state.hasDismissed,
      }),
    },
  ),
);
